import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo } from "react";

import { useAppSelector } from "app/hooks";
import {
  SCHEDULING_V3_DEV_ACCESS,
  SCHEDULING_V3_FEATURE
} from "constants/feature-flags";
import {
  PRE_AUTH_USER_ID,
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";
import { useGetSchedulingOptInQuery } from "features/scheduling-v3/data/api/scheduling-settings.api";
import {
  readSchedulingSettingsCache,
  writeSchedulingSettingsCache
} from "features/scheduling-v3/data/settings-cache";
import {
  SETTINGS_DOCUMENT_VERSION,
  readOptInDocument,
  readOptInState
} from "features/scheduling-v3/domain/settings-document";
import useUserRoles from "hooks/use-user-roles.hook";

export interface SchedulerChoice {
  /**
   * The switch is offered: the flag is on for this person, their practice hasn't turned the
   * new scheduler off (ENG-2149), and they can use the scheduler.
   */
  offered: boolean;
  /** `pending` only while an offered person's choice is being read. */
  status: "pending" | "settled";
  /** Scheduling opens the new scheduler. Never true unless offered: the flag is the brake. */
  useV3: boolean;
  /** The stored opt-in document, for the writers to build on; `null` when there is none. */
  document: unknown;
  /**
   * The real user and practice have loaded. Until then `offered` is false for everyone, so
   * the route gate must wait on this (boundedly) rather than read that as "classic".
   */
  userLoaded: boolean;
  /**
   * `document` is the server's own record, from a read that succeeded. It is not while
   * answering from the D14 mirror (which holds no reasons) or after a failed read (which holds
   * nothing): a write must never build on those, or it would erase the person's history.
   */
  fromServer: boolean;
  /**
   * Development access (`scheduling-v-3-dev-access`): V3 gets its own menu entry and
   * `/scheduling-v3` opens whatever the choice. Everything else — the offer, the redirect from
   * classic, the opt-out question — is exactly what everyone offered the choice sees.
   */
  devAccess: boolean;
}

/**
 * D14: a session-only mirror of the choice, so a warm load paints the chosen scheduler without
 * waiting a round trip. It reuses ENG-2047's cache functions under its own key — per person,
 * like the row, and outside that module's sweep prefix so V3's sweep never removes it.
 */
const mirrorKey = (userId: number) => `suno.scheduler-choice.c1.${userId}`;

/**
 * Only what routing and the offer need: never the reasons or notes people gave, which could
 * name a patient — exam-room computers are shared (the same rule as ENG-2047's cache).
 */
function mirrorable(document: unknown): Record<string, unknown> {
  const { optIn, notNow, optOuts } = readOptInState(document);
  const last = optOuts.at(-1);
  return {
    v: SETTINGS_DOCUMENT_VERSION,
    opt_in: optIn,
    not_now: { count: notNow.count, last_at: notNow.lastAt },
    opt_outs: last
      ? [
          {
            at: last.at,
            practice_id: last.practiceId,
            reason: null,
            detail: null
          }
        ]
      : []
  };
}

/**
 * ENG-1981: the one place that combines the `scheduling-v-3-feature` flag with the person's
 * own choice. Every reader — routes, side menu, chart entries — asks this, never the flag.
 *
 * Whether LaunchDarkly has answered at all is the route gate's job (`useRouteGateReady`,
 * which gives up after a grace period); this hook only waits on its own read, which has a
 * timeout, so nothing here can hold a page forever. A failed or empty read is "classic".
 */
export function useSchedulerChoice(): SchedulerChoice {
  const flags = useFlags();
  const user = useAppSelector(selectCurrentUser);
  const practice = useAppSelector(selectCurrentPractice);
  const { isReportsOnlyUser } = useUserRoles();

  const userId = user?.id;
  const practiceId = practice?.id;
  const userLoaded =
    userId != null && userId !== PRE_AUTH_USER_ID && practiceId != null;
  // ENG-2149: a practice admin can turn the choice off for everyone. `!== false`, so a backend
  // without the field (before migration 0476) and a practice that never touched it stay on.
  // While it's off the person's own document isn't read (the query below skips), so it survives
  // untouched and the choice comes back when the switch does.
  const offered =
    flags[SCHEDULING_V3_FEATURE] === true &&
    practice?.new_scheduler_enabled !== false &&
    userLoaded &&
    !isReportsOnlyUser;
  const devAccess =
    flags[SCHEDULING_V3_DEV_ACCESS] === true &&
    userLoaded &&
    !isReportsOnlyUser;

  // `currentData`, not `data`: after a practice switch `data` still holds the previous
  // practice's answer until the new read lands, and `skip` never clears it.
  const { currentData, isError } = useGetSchedulingOptInQuery(
    { userId: userId ?? 0, practiceId: practiceId ?? 0 },
    { skip: !offered }
  );

  const key = offered && userId != null ? mirrorKey(userId) : null;
  // Read once per person: after that the server's answer (or the cache update a switch
  // makes) is what counts.
  const mirrored = useMemo(
    () => (key ? (readSchedulingSettingsCache(key)?.document ?? null) : null),
    [key]
  );
  useEffect(() => {
    if (!key || currentData === undefined || isError) return;
    writeSchedulingSettingsCache(key, {
      c: 1,
      v: SETTINGS_DOCUMENT_VERSION,
      document: mirrorable(currentData),
      updated_at: new Date().toISOString()
    });
  }, [key, currentData, isError]);

  if (!offered) {
    return {
      offered,
      status: "settled",
      useV3: false,
      document: null,
      userLoaded,
      fromServer: false,
      devAccess
    };
  }
  const settled = currentData !== undefined || isError;
  if (!settled && mirrored) {
    // The read is still in flight: answer from what the server said earlier this session.
    return {
      offered,
      status: "settled",
      useV3: readOptInDocument(mirrored),
      document: mirrored,
      userLoaded,
      fromServer: false,
      devAccess
    };
  }
  return {
    offered,
    status: settled ? "settled" : "pending",
    useV3: !isError && readOptInDocument(currentData),
    document: isError ? null : (currentData ?? null),
    userLoaded,
    fromServer: currentData !== undefined && !isError,
    devAccess
  };
}
